/**
 * @zendir/ui - Responsive Utilities
 *
 * Helpers for resolving responsive values and spacing tokens to CSS values.
 * Used internally by layout primitives (Box, Flex, Grid, Stack).
 */

import type { ThemeTokens } from "../../theme";
import type { Breakpoint, ResponsiveValue } from "./useBreakpoint";
import { resolveResponsiveValue } from "./useBreakpoint";

/** Spacing token names matching ThemeTokens.spacing */
export type SpacingToken =
  | "xxs"
  | "xs"
  | "sm"
  | "smd"
  | "md"
  | "mdl"
  | "lg"
  | "xl"
  | "xxl";

/** Value that can be a spacing token, a CSS string, or a number (px) */
type SpacingInput = SpacingToken | string | number;

/**
 * Resolve a spacing value to a CSS string.
 * - SpacingToken ('xs', 'sm', etc.) → looks up token value
 * - number → converts to px string
 * - string → passes through (e.g., '1rem', '50%')
 */
export function resolveSpacing(
  value: SpacingInput | undefined,
  tokens: ThemeTokens,
): string | undefined {
  if (value === undefined || value === null) return undefined;

  // Spacing token
  if (typeof value === "string" && value in tokens.spacing) {
    return tokens.spacing[value as SpacingToken];
  }

  // Number → px
  if (typeof value === "number") {
    return `${value}px`;
  }

  // CSS string passthrough
  return value;
}

/**
 * Resolve a responsive spacing value at the current breakpoint.
 */
export function resolveResponsiveSpacing(
  value: ResponsiveValue<SpacingInput> | undefined,
  breakpoint: Breakpoint,
  tokens: ThemeTokens,
): string | undefined {
  if (value === undefined || value === null) return undefined;

  const resolved = Array.isArray(value)
    ? resolveResponsiveValue(value, breakpoint)
    : value;

  return resolveSpacing(resolved, tokens);
}

/**
 * Resolve a responsive value (non-spacing) at the current breakpoint.
 */
export function resolveResponsive<T>(
  value: ResponsiveValue<T> | undefined,
  breakpoint: Breakpoint,
): T | undefined {
  if (value === undefined || value === null) return undefined;
  return Array.isArray(value)
    ? resolveResponsiveValue(value, breakpoint)
    : value;
}
